import { Injectable } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PosthogAnalyticsService {
  private initialized = false;
  private enabled = false;

  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    const apiKey = environment.posthogApiKey?.trim();
    const apiHost = environment.posthogApiHost?.trim();
    if (!apiKey || !apiHost) {
      return;
    }

    posthog.init(apiKey, {
      api_host: apiHost,
      person_profiles: 'identified_only',
      capture_performance: false,
      // We want clear business events only. Avoid noisy click autocapture.
      autocapture: false,
      // We will emit our own page events (`page_viewed`, `page_left`).
      capture_pageview: false,
      loaded: (ph) => {
        ph.register({
          portal: 'gp_portal',
          app_env: environment.layer,
        });
      },
      before_send: (event) => {
        if (event?.event === '$web_vitals') {
          return null;
        }
        // Remove query parameters to prevent leaking identifiers/tokens.
        const properties = event?.properties;
        const currentUrl = properties?.['$current_url'];
        if (properties && currentUrl) {
          properties['$current_url'] = String(currentUrl).split('?')[0];
        }
        return event;
      },
      session_recording: {
        maskAllInputs: true,
        // rrweb default block class. Sensitive lists/messages add this in templates.
        blockClass: 'ph-no-capture',
        // Block tagged regions plus remaining full-PII widgets.
        blockSelector: [
          '.ph-no-capture',
          'app-pdf-apryse',
          'apryse-webviewer',
          'app-document-preview-dialog',
          'app-onboarding-internal-notes',
          'app-investor-detail',
        ].join(', '),
      },
    });
    this.initialized = true;
    this.enabled = true;
  }

  isEnabled(): boolean {
    return this.enabled && this.initialized;
  }

  identify(distinctId: string, properties: Record<string, unknown> = {}): void {
    if (!this.isEnabled() || !distinctId) return;
    try {
      posthog.identify(String(distinctId), properties);
    } catch {
      // no-op
    }
  }

  capture(event: string, properties: Record<string, unknown> = {}): void {
    if (!this.isEnabled() || !event) return;
    try {
      posthog.capture(event, properties);
    } catch {
      // no-op
    }
  }
}
